import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  VpnKey as VpnKeyIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  vaultApi,
  VaultConfig,
  VaultAWSPath,
  VaultAWSRoleInfo,
  VaultKVPath,
  VaultKVSecret,
  VaultAuthRequest,
  VaultAWSCredentials,
  EKSClusterInfo,
  EKSKubeconfigRequest,
  eksApi,
} from '../services/integrations-api';

export const VaultManager: React.FC = () => {
  const [instances, setInstances] = useState<VaultConfig[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<VaultConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [pathDialogOpen, setPathDialogOpen] = useState(false);
  const [kvDialogOpen, setKvDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [credentials, setCredentials] = useState<{ [pathId: string]: VaultAWSCredentials }>({});
  const [kvSecrets, setKvSecrets] = useState<{ [pathId: string]: VaultKVSecret }>({});
  const [discoveringRoles, setDiscoveringRoles] = useState(false);
  const [discoveredRoles, setDiscoveredRoles] = useState<{ stsRoles: VaultAWSRoleInfo[]; iamRoles: VaultAWSRoleInfo[] } | null>(null);
  const [eksClusters, setEksClusters] = useState<EKSClusterInfo[]>([]);
  const [discoveringEKS, setDiscoveringEKS] = useState(false);
  const [eksDialogOpen, setEksDialogOpen] = useState(false);
  const [eksForm, setEksForm] = useState({
    vaultPathId: '',
    region: 'us-east-1',
    selectedCluster: '',
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<VaultConfig>>({});

  const [authForm, setAuthForm] = useState<VaultAuthRequest>({
    instanceName: '',
    vaultUrl: '',
    authMethod: 'token',
    token: '',
    roleId: '',
    secretId: '',
    oidcRole: '',
  });

  const [pathForm, setPathForm] = useState<Omit<VaultAWSPath, 'id' | 'vaultInstanceId'>>({
    name: '',
    mountPoint: 'aws',
    path: '',
    type: 'sts',
    ttl: 3600,
    enabled: true,
  });

  const [kvForm, setKvForm] = useState<Omit<VaultKVPath, 'id' | 'vaultInstanceId'>>({
    name: '',
    mountPoint: 'secret',
    path: '',
    version: 2,
    enabled: true,
  });

  useEffect(() => {
    loadInstances();
  }, []);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const data = await vaultApi.getAllInstances();
      setInstances(data);

      // Update selected instance to get fresh data
      if (selectedInstance) {
        const updatedInstance = data.find(i => i.id === selectedInstance.id);
        if (updatedInstance) {
          setSelectedInstance(updatedInstance);
        }
      } else if (data.length > 0) {
        // Select first instance if none selected
        setSelectedInstance(data[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load Vault instances');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setLoading(true);
      setError(null);

      await vaultApi.authenticate(authForm);

      // Reload instances
      await loadInstances();

      // Only close dialog and show success if everything worked
      setSuccess('Successfully authenticated with Vault!');
      setAuthDialogOpen(false);
      resetAuthForm();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Authentication failed');
      // Don't close dialog on error so user can see the error message
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInstance = async (instanceId: string) => {
    if (!confirm('Are you sure you want to delete this Vault instance?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await vaultApi.deleteInstance(instanceId);

      // If deleted instance was selected, clear selection
      if (selectedInstance?.id === instanceId) {
        setSelectedInstance(null);
      }

      // Reload instances
      await loadInstances();
      setSuccess('Vault instance deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete instance');
    } finally {
      setLoading(false);
    }
  };

  const resetAuthForm = () => {
    setAuthForm({
      instanceName: '',
      vaultUrl: '',
      authMethod: 'token',
      token: '',
      roleId: '',
      secretId: '',
      oidcRole: '',
    });
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setError(null);

      const result = await vaultApi.testConnection(
        authForm.vaultUrl || selectedInstance?.vaultUrl || '',
        authForm.token
      );

      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleAddPath = async () => {
    try {
      setLoading(true);
      setError(null);

      await vaultApi.addAWSPath(pathForm);

      setSuccess(`AWS path "${pathForm.name}" added successfully!`);
      setPathDialogOpen(false);
      resetPathForm();

      // Reload instances to get updated path list
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add AWS path');
    } finally {
      setLoading(false);
    }
  };

  const resetPathForm = () => {
    setPathForm({
      name: '',
      mountPoint: 'aws',
      path: '',
      type: 'sts',
      ttl: 3600,
      enabled: true,
    });
    setDiscoveredRoles(null);
  };

  const handleDiscoverRoles = async () => {
    try {
      setDiscoveringRoles(true);
      setError(null);

      const roles = await vaultApi.discoverRoles();
      setDiscoveredRoles(roles);
      setSuccess(`Discovered ${roles.stsRoles.length} STS roles and ${roles.iamRoles.length} IAM roles`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to discover roles');
    } finally {
      setDiscoveringRoles(false);
    }
  };

  const handleRoleSelect = (roleInfoJson: string) => {
    // Parse the role info from the select value
    const roleInfo: VaultAWSRoleInfo = JSON.parse(roleInfoJson);

    // Auto-populate mount point and path from discovered role
    setPathForm({
      ...pathForm,
      mountPoint: roleInfo.mountPoint,
      path: roleInfo.path,
      name: pathForm.name || `${roleInfo.mountPoint}/${roleInfo.name}` // Suggest a display name
    });
  };

  const handleRemovePath = async (pathId: string) => {
    if (!confirm('Are you sure you want to remove this AWS path?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await vaultApi.removeAWSPath(pathId);

      setSuccess('AWS path removed successfully');
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to remove AWS path');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePath = async (pathId: string, enabled: boolean) => {
    try {
      setLoading(true);
      setError(null);

      await vaultApi.toggleAWSPath(pathId, enabled);

      setSuccess(enabled ? 'AWS path enabled' : 'AWS path disabled');
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to toggle AWS path');
    } finally {
      setLoading(false);
    }
  };

  const handleGetCredentials = async (pathId: string, forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const creds = await vaultApi.getAWSCredentials(pathId, forceRefresh);

      setCredentials({ ...credentials, [pathId]: creds });
      setSuccess(forceRefresh ? 'Credentials refreshed' : 'Credentials retrieved');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to get credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewToken = async () => {
    try {
      setLoading(true);
      setError(null);

      await vaultApi.renewToken();

      setSuccess('Vault token renewed successfully');
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to renew token');
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    try {
      setLoading(true);
      setError(null);

      await vaultApi.clearCache();

      setSuccess('Credential cache cleared');
      setCredentials({});
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to clear cache');
    } finally {
      setLoading(false);
    }
  };

  // KV Secrets Handlers
  const handleAddKVPath = async () => {
    if (!selectedInstance) {
      setError('No Vault instance selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await vaultApi.addKVPath(selectedInstance.id, kvForm);

      setSuccess('KV path added successfully');
      setKvDialogOpen(false);
      setKvForm({ name: '', mountPoint: 'secret', path: '', version: 2, enabled: true });
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add KV path');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveKVPath = async (pathId: string) => {
    if (!confirm('Are you sure you want to remove this KV path?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await vaultApi.removeKVPath(pathId);

      setSuccess('KV path removed successfully');
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to remove KV path');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleKVPath = async (pathId: string, enabled: boolean) => {
    try {
      setLoading(true);
      setError(null);

      await vaultApi.toggleKVPath(pathId, enabled);

      setSuccess(enabled ? 'KV path enabled' : 'KV path disabled');
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to toggle KV path');
    } finally {
      setLoading(false);
    }
  };

  const handleReadKVSecret = async (pathId: string) => {
    try {
      setLoading(true);
      setError(null);

      const secret = await vaultApi.readKVSecret(pathId);

      setKvSecrets({ ...kvSecrets, [pathId]: secret });
      setSuccess('Secret data retrieved');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to read secret');
    } finally {
      setLoading(false);
    }
  };

  // EKS Handlers
  const handleDiscoverEKS = async () => {
    if (!eksForm.vaultPathId) {
      setError('Please select a Vault AWS path');
      return;
    }

    try {
      setDiscoveringEKS(true);
      setError(null);

      const clusters = await eksApi.discoverClusters(eksForm.vaultPathId, eksForm.region);
      setEksClusters(clusters);
      setSuccess(`Discovered ${clusters.length} EKS cluster${clusters.length !== 1 ? 's' : ''}`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to discover EKS clusters');
    } finally {
      setDiscoveringEKS(false);
    }
  };

  const handleGenerateEKSKubeconfig = async () => {
    if (!eksForm.vaultPathId || !eksForm.selectedCluster) {
      setError('Please select a Vault AWS path and EKS cluster');
      return;
    }

    const cluster = eksClusters.find(c => c.name === eksForm.selectedCluster);
    if (!cluster) {
      setError('Selected cluster not found');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const kubeconfigPath = await eksApi.generateKubeconfig({
        clusterName: cluster.name,
        region: cluster.region,
        vaultPathId: eksForm.vaultPathId,
      });

      setSuccess(`EKS kubeconfig generated at: ${kubeconfigPath}`);
      setEksDialogOpen(false);
      setEksForm({ vaultPathId: '', region: 'us-east-1', selectedCluster: '' });
      setEksClusters([]);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate EKS kubeconfig');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInstance = (instance: VaultConfig) => {
    setEditForm({
      name: instance.name,
      vaultUrl: instance.vaultUrl,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedInstance) return;

    try {
      setLoading(true);
      setError(null);

      await vaultApi.updateInstance(selectedInstance.id, editForm);

      await loadInstances();
      setSuccess('Vault instance updated successfully');
      setEditDialogOpen(false);
      setEditForm({});
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update instance');
    } finally {
      setLoading(false);
    }
  };

  if (loading && instances.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Vault Integration ({instances.length} instance{instances.length !== 1 ? 's' : ''})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAuthDialogOpen(true)}
          disabled={loading}
        >
          Add Vault Instance
        </Button>
      </Box>

      {/* Instance Selector */}
      {instances.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Vault Instance</InputLabel>
                  <Select
                    value={selectedInstance?.id || ''}
                    onChange={(e) => {
                      const instance = instances.find(i => i.id === e.target.value);
                      setSelectedInstance(instance || null);
                    }}
                    label="Select Vault Instance"
                  >
                    {instances.map((instance) => (
                      <MenuItem key={instance.id} value={instance.id}>
                        <Box>
                          <Typography variant="body1">{instance.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {instance.vaultUrl}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={1}>
                  {selectedInstance && (
                    <>
                      <Tooltip title="Edit this Vault instance">
                        <IconButton
                          onClick={() => handleEditInstance(selectedInstance)}
                          disabled={loading}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Renew Vault token">
                        <IconButton onClick={handleRenewToken} disabled={loading} size="small">
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clear credential cache">
                        <IconButton onClick={handleClearCache} disabled={loading} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete this Vault instance">
                        <IconButton
                          onClick={() => handleDeleteInstance(selectedInstance.id)}
                          disabled={loading}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
            {error}
          </Typography>
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Configuration Status */}
      {selectedInstance ? (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Vault URL:</strong> {selectedInstance.vaultUrl}
            </Typography>
            <Typography variant="body2">
              <strong>Auth Method:</strong> {selectedInstance.authMethod.toUpperCase()}
            </Typography>
            {selectedInstance.authMethod === 'oidc' && selectedInstance.oidcRole && (
              <Typography variant="body2">
                <strong>OIDC Role:</strong> {selectedInstance.oidcRole}
              </Typography>
            )}
            {selectedInstance.lastAuthenticated && (
              <Typography variant="body2">
                <strong>Last Authenticated:</strong> {new Date(selectedInstance.lastAuthenticated).toLocaleString()}
              </Typography>
            )}
            {selectedInstance.vaultTokenExpiration && (
              <Typography variant="body2">
                <strong>Token Expires:</strong> {new Date(selectedInstance.vaultTokenExpiration).toLocaleString()}
              </Typography>
            )}
          </Alert>

          {/* AWS Paths */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">
              AWS Secrets Paths ({selectedInstance.awsSecretsEngine.paths.length})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setPathDialogOpen(true)}
              disabled={loading}
            >
              Add AWS Path
            </Button>
          </Box>

          {selectedInstance.awsSecretsEngine.paths.length === 0 ? (
            <Alert severity="warning">
              No AWS secrets paths configured. Click "Add AWS Path" to get started.
            </Alert>
          ) : (
            selectedInstance.awsSecretsEngine.paths.map((path) => (
              <Accordion key={path.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>{path.name}</Typography>
                      {path.enabled && (
                        <Chip
                          label="Enabled"
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon />}
                        />
                      )}
                      <Chip label={path.type.toUpperCase()} size="small" color="primary" />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Mount Point:</strong> /{path.mountPoint || selectedInstance.awsSecretsEngine.mountPoint}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Path:</strong> {path.path}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        <strong>Full Path:</strong> /{path.mountPoint || config.awsSecretsEngine.mountPoint}/{path.path}
                      </Typography>
                      {path.ttl && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>TTL:</strong> {path.ttl} seconds
                        </Typography>
                      )}
                    </Grid>

                    {/* Show credentials if fetched */}
                    {credentials[path.id] && (
                      <Grid item xs={12}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              <VpnKeyIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                              Current Credentials
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              <strong>Access Key:</strong> {credentials[path.id].accessKeyId}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              <strong>Secret Key:</strong> {credentials[path.id].secretAccessKey.substring(0, 20)}...
                            </Typography>
                            {credentials[path.id].sessionToken && (
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                <strong>Session Token:</strong> {credentials[path.id].sessionToken?.substring(0, 30)}...
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              <strong>Expires:</strong> {new Date(credentials[path.id].expiresAt).toLocaleString()}
                            </Typography>
                            {credentials[path.id].renewable && (
                              <Chip label="Auto-Renewable" size="small" color="success" sx={{ mt: 1 }} />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={path.enabled}
                              onChange={(e) => handleTogglePath(path.id, e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label={path.enabled ? 'Enabled' : 'Disabled'}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleGetCredentials(path.id, false)}
                          disabled={loading || !path.enabled}
                        >
                          Get Credentials
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleGetCredentials(path.id, true)}
                          disabled={loading || !path.enabled}
                        >
                          Force Refresh
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemovePath(path.id)}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))
          )}

          {/* KV Secrets Paths */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={3}>
            <Typography variant="subtitle1">
              KV Secrets Paths ({selectedInstance.kvPaths?.length || 0})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setKvDialogOpen(true)}
              disabled={loading}
            >
              Add KV Path
            </Button>
          </Box>

          {(!selectedInstance.kvPaths || selectedInstance.kvPaths.length === 0) ? (
            <Alert severity="info">
              No KV secrets paths configured. Click "Add KV Path" to configure a Key-Value secrets path.
            </Alert>
          ) : (
            selectedInstance.kvPaths.map((kvPath) => (
              <Accordion key={kvPath.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>{kvPath.name}</Typography>
                      {kvPath.enabled && (
                        <Chip
                          label="Enabled"
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon />}
                        />
                      )}
                      <Chip label={`KV v${kvPath.version || 2}`} size="small" color="info" />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Mount Point:</strong> /{kvPath.mountPoint}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Path:</strong> {kvPath.path}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        <strong>Full Path:</strong> /{kvPath.mountPoint}/{kvPath.version === 1 ? '' : 'data/'}{kvPath.path}
                      </Typography>
                    </Grid>

                    {/* Show secret data if fetched */}
                    {kvSecrets[kvPath.id] && (
                      <Grid item xs={12}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              <VpnKeyIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                              Secret Data
                            </Typography>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', backgroundColor: 'action.hover', p: 1, borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                              <pre style={{ margin: 0 }}>{JSON.stringify(kvSecrets[kvPath.id].data, null, 2)}</pre>
                            </Box>
                            {kvSecrets[kvPath.id].metadata && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Version {kvSecrets[kvPath.id].metadata?.version} • Created {new Date(kvSecrets[kvPath.id].metadata?.createdTime || '').toLocaleString()}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={kvPath.enabled}
                              onChange={(e) => handleToggleKVPath(kvPath.id, e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label={kvPath.enabled ? 'Enabled' : 'Disabled'}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleReadKVSecret(kvPath.id)}
                          disabled={loading || !kvPath.enabled}
                        >
                          Read Secret
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveKVPath(kvPath.id)}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))
          )}

          {/* EKS Cluster Integration */}
          <Box mt={4}>
            <Typography variant="subtitle1" gutterBottom>
              EKS Cluster Kubeconfig Generation
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Use Vault AWS credentials to discover and generate kubeconfigs for EKS clusters
            </Alert>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setEksDialogOpen(true)}
              disabled={loading || selectedInstance.awsSecretsEngine.paths.length === 0}
            >
              Generate EKS Kubeconfig
            </Button>
            {selectedInstance.awsSecretsEngine.paths.length === 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Add an AWS secrets path first to use EKS integration
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Alert severity="info">
          No Vault instances configured. Click "Add Vault Instance" to get started.
        </Alert>
      )}

      {/* Authentication Dialog */}
      <Dialog
        open={authDialogOpen}
        onClose={() => !loading && setAuthDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Connect to Vault</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                {error}
              </Typography>
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instance Name"
                placeholder="e.g., Production Vault"
                value={authForm.instanceName}
                onChange={(e) => setAuthForm({ ...authForm, instanceName: e.target.value })}
                helperText="A friendly name to identify this Vault instance"
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vault URL"
                placeholder="https://vault.example.com"
                value={authForm.vaultUrl}
                onChange={(e) => setAuthForm({ ...authForm, vaultUrl: e.target.value })}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Authentication Method</InputLabel>
                <Select
                  value={authForm.authMethod}
                  label="Authentication Method"
                  onChange={(e) => setAuthForm({ ...authForm, authMethod: e.target.value as any })}
                  disabled={loading}
                >
                  <MenuItem value="token">Token</MenuItem>
                  <MenuItem value="approle">AppRole</MenuItem>
                  <MenuItem value="aws">AWS IAM</MenuItem>
                  <MenuItem value="oidc">OIDC</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {authForm.authMethod === 'token' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vault Token"
                  type="password"
                  value={authForm.token}
                  onChange={(e) => setAuthForm({ ...authForm, token: e.target.value })}
                  required
                  disabled={loading}
                />
              </Grid>
            )}

            {authForm.authMethod === 'approle' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Role ID"
                    value={authForm.roleId}
                    onChange={(e) => setAuthForm({ ...authForm, roleId: e.target.value })}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Secret ID"
                    type="password"
                    value={authForm.secretId}
                    onChange={(e) => setAuthForm({ ...authForm, secretId: e.target.value })}
                    required
                    disabled={loading}
                  />
                </Grid>
              </>
            )}

            {authForm.authMethod === 'oidc' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Role"
                    value={authForm.oidcRole}
                    onChange={(e) => setAuthForm({ ...authForm, oidcRole: e.target.value })}
                    required
                    disabled={loading}
                    helperText="The OIDC role configured in Vault"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Vault Token"
                    type="password"
                    value={authForm.token}
                    onChange={(e) => setAuthForm({ ...authForm, token: e.target.value })}
                    required
                    disabled={loading}
                    helperText="Obtain this token by running: vault login -method=oidc role=<role>"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>OIDC Authentication:</strong> Please authenticate using Vault CLI first:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1, wordBreak: 'break-all' }}>
                      VAULT_ADDR={authForm.vaultUrl || 'https://vault.example.com'} vault login -method=oidc role=<strong>{authForm.oidcRole || 'your-role'}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Then paste the resulting token above.
                    </Typography>
                  </Alert>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleTestConnection}
                disabled={loading || testingConnection || !authForm.vaultUrl}
                startIcon={testingConnection ? <CircularProgress size={16} /> : undefined}
              >
                Test Connection
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAuthDialogOpen(false); resetAuthForm(); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAuthenticate}
            variant="contained"
            disabled={loading || !authForm.vaultUrl}
          >
            {loading ? <CircularProgress size={24} /> : 'Authenticate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add AWS Path Dialog */}
      <Dialog
        open={pathDialogOpen}
        onClose={() => !loading && setPathDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add AWS Secrets Path</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" gap={1} mb={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleDiscoverRoles}
                  disabled={loading || discoveringRoles}
                  startIcon={discoveringRoles ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  {discoveringRoles ? 'Discovering...' : 'Discover Roles from Vault'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Credential Type</InputLabel>
                <Select
                  value={pathForm.type}
                  label="Credential Type"
                  onChange={(e) => setPathForm({ ...pathForm, type: e.target.value as any })}
                  disabled={loading}
                >
                  <MenuItem value="sts">STS (Temporary Credentials)</MenuItem>
                  <MenuItem value="iam">IAM (Long-lived Credentials)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {discoveredRoles && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Role</InputLabel>
                  <Select
                    label="Select Role"
                    onChange={(e) => handleRoleSelect(e.target.value)}
                    disabled={loading}
                    defaultValue=""
                  >
                    <MenuItem value="" disabled>
                      <em>Choose a discovered role...</em>
                    </MenuItem>
                    {(pathForm.type === 'sts' ? discoveredRoles.stsRoles : discoveredRoles.iamRoles).map((roleInfo) => (
                      <MenuItem key={`${roleInfo.mountPoint}/${roleInfo.name}`} value={JSON.stringify(roleInfo)}>
                        <Box>
                          <Typography variant="body1">{roleInfo.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mount: {roleInfo.mountPoint} | Path: {roleInfo.path} | Type: {roleInfo.credentialType}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                placeholder="S3 Admin STS"
                value={pathForm.name}
                onChange={(e) => setPathForm({ ...pathForm, name: e.target.value })}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mount Point"
                placeholder="aws"
                value={pathForm.mountPoint}
                onChange={(e) => setPathForm({ ...pathForm, mountPoint: e.target.value })}
                required
                disabled={loading}
                helperText="AWS secrets engine mount point (e.g., 'aws', 'awsdev', 'awsprod')"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vault Path"
                placeholder="creds/s3-admin"
                value={pathForm.path}
                onChange={(e) => setPathForm({ ...pathForm, path: e.target.value })}
                required
                disabled={loading}
                helperText={`Full path: /${pathForm.mountPoint}/${pathForm.path}`}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Path Format:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • For STS (temporary): <strong>creds/role-name</strong>
                </Typography>
                <Typography variant="body2">
                  • For IAM (long-lived): <strong>iam/role-name</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                  Example: If your role is "s3-admin", use "creds/s3-admin" for STS credentials
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="TTL (seconds)"
                type="number"
                value={pathForm.ttl}
                onChange={(e) => setPathForm({ ...pathForm, ttl: parseInt(e.target.value) })}
                disabled={loading}
                helperText="Time to live for credentials (leave blank for default)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={pathForm.enabled}
                    onChange={(e) => setPathForm({ ...pathForm, enabled: e.target.checked })}
                    disabled={loading}
                  />
                }
                label="Enable immediately"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPathDialogOpen(false); resetPathForm(); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddPath}
            variant="contained"
            disabled={loading || !pathForm.name || !pathForm.path}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Path'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add KV Path Dialog */}
      <Dialog
        open={kvDialogOpen}
        onClose={() => !loading && setKvDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add KV Secrets Path</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                placeholder="Production S3 Credentials"
                value={kvForm.name}
                onChange={(e) => setKvForm({ ...kvForm, name: e.target.value })}
                required
                disabled={loading}
                helperText="A friendly name for this KV secrets path"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mount Point"
                placeholder="secret"
                value={kvForm.mountPoint}
                onChange={(e) => setKvForm({ ...kvForm, mountPoint: e.target.value })}
                required
                disabled={loading}
                helperText="KV engine mount point (e.g., 'secret', 'kv')"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>KV Version</InputLabel>
                <Select
                  value={kvForm.version || 2}
                  label="KV Version"
                  onChange={(e) => setKvForm({ ...kvForm, version: e.target.value as number })}
                  disabled={loading}
                >
                  <MenuItem value={1}>KV v1</MenuItem>
                  <MenuItem value={2}>KV v2</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vault Path"
                placeholder="aws/s3-credentials"
                value={kvForm.path}
                onChange={(e) => setKvForm({ ...kvForm, path: e.target.value })}
                required
                disabled={loading}
                helperText={`Full read path: /${kvForm.mountPoint}/${kvForm.version === 1 ? '' : 'data/'}${kvForm.path}`}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>KV Path Format:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • KV v1: Direct path to secret (e.g., <code>aws/credentials</code>)
                </Typography>
                <Typography variant="body2">
                  • KV v2: Path without "data/" prefix (e.g., <code>aws/credentials</code> reads from <code>/{kvForm.mountPoint}/data/aws/credentials</code>)
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                  The secret at this path should contain key-value pairs like access_key_id, secret_access_key, etc.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={kvForm.enabled}
                    onChange={(e) => setKvForm({ ...kvForm, enabled: e.target.checked })}
                    disabled={loading}
                  />
                }
                label="Enable immediately"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setKvDialogOpen(false); setKvForm({ name: '', mountPoint: 'secret', path: '', version: 2, enabled: true }); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddKVPath}
            variant="contained"
            disabled={loading || !kvForm.name || !kvForm.mountPoint || !kvForm.path}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Path'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EKS Kubeconfig Generation Dialog */}
      <Dialog
        open={eksDialogOpen}
        onClose={() => !loading && setEksDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generate EKS Kubeconfig</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Vault AWS Path</InputLabel>
                <Select
                  value={eksForm.vaultPathId}
                  label="Vault AWS Path"
                  onChange={(e) => setEksForm({ ...eksForm, vaultPathId: e.target.value })}
                  disabled={loading || discoveringEKS}
                >
                  {selectedInstance?.awsSecretsEngine.paths.filter(p => p.enabled).map((path) => (
                    <MenuItem key={path.id} value={path.id}>
                      {path.name} ({path.mountPoint}/{path.path})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="AWS Region"
                value={eksForm.region}
                onChange={(e) => setEksForm({ ...eksForm, region: e.target.value })}
                disabled={loading || discoveringEKS}
                helperText="AWS region to discover EKS clusters"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleDiscoverEKS}
                disabled={loading || discoveringEKS || !eksForm.vaultPathId}
                startIcon={discoveringEKS ? <CircularProgress size={16} /> : <RefreshIcon />}
                sx={{ height: '56px' }}
              >
                {discoveringEKS ? 'Discovering...' : 'Discover Clusters'}
              </Button>
            </Grid>

            {eksClusters.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>EKS Cluster</InputLabel>
                  <Select
                    value={eksForm.selectedCluster}
                    label="EKS Cluster"
                    onChange={(e) => setEksForm({ ...eksForm, selectedCluster: e.target.value })}
                    disabled={loading}
                  >
                    {eksClusters.map((cluster) => (
                      <MenuItem key={cluster.name} value={cluster.name}>
                        <Box>
                          <Typography variant="body1">{cluster.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Version: {cluster.version} | Status: {cluster.status}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>How it works:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  1. Select a Vault AWS path with valid credentials<br />
                  2. Choose the AWS region where your EKS clusters are located<br />
                  3. Click "Discover Clusters" to find all EKS clusters<br />
                  4. Select a cluster and generate its kubeconfig
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEksDialogOpen(false); setEksForm({ vaultPathId: '', region: 'us-east-1', selectedCluster: '' }); setEksClusters([]); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateEKSKubeconfig}
            variant="contained"
            disabled={loading || !eksForm.vaultPathId || !eksForm.selectedCluster}
          >
            {loading ? <CircularProgress size={24} /> : 'Generate Kubeconfig'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Instance Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Vault Instance</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instance Name"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vault URL"
                  value={editForm.vaultUrl || ''}
                  onChange={(e) => setEditForm({ ...editForm, vaultUrl: e.target.value })}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading || !editForm.name}>
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
